/**
 * useHelp.fetch - Fonctions de fetch & persistance du Help Drawer.
 *
 * Extraites de `useHelp.ts` (extraction mécanique, zéro changement de
 * comportement). Utilisées uniquement par le hub `useHelp.ts`.
 */

import { API_BASE, apiFetch } from '@/lib/api';
import { type HelpArticle, type HelpDrawerMode } from './useHelp.types';

export const HELP_MODE_STORAGE_KEY = 'argalis-help-mode';
export const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)'; // Tailwind `lg`

export function readStoredHelpMode(): HelpDrawerMode {
    // Défaut = ancré (la page se compresse) ; l'overlay reste un choix explicite.
    if (typeof window === 'undefined') return 'docked';
    return window.localStorage.getItem(HELP_MODE_STORAGE_KEY) === 'overlay' ? 'overlay' : 'docked';
}

// ── Query function ───────────────────────────────────────────────────

export async function fetchHelpArticles(
    normalizedContexts: string[],
    lang: string,
): Promise<HelpArticle[]> {
    if (normalizedContexts.length === 0) {
        // Pas de contexte, charger tous les articles
        const params = new URLSearchParams({ lang });
        const response = await apiFetch(`${API_BASE}/knowledge/articles?${params}`);
        if (!response.ok) throw new Error("Failed to fetch articles");
        const data = await response.json();
        return data.items || [];
    }

    // Fetch en parallèle pour chaque contexte
    const fetchPromises = normalizedContexts.map(async (ctx) => {
        const params = new URLSearchParams({ lang, tag: ctx });
        const response = await apiFetch(`${API_BASE}/knowledge/articles?${params}`);
        if (!response.ok) return [];
        const data = await response.json();
        return (data.items || []) as HelpArticle[];
    });

    const results = await Promise.all(fetchPromises);

    // Merge et déduplique les articles (par slug)
    const allArticles: HelpArticle[] = [];
    const seenSlugs = new Set<string>();

    for (const articleList of results) {
        for (const article of articleList) {
            if (!seenSlugs.has(article.slug)) {
                seenSlugs.add(article.slug);
                allArticles.push(article);
            }
        }
    }

    return allArticles;
}
